import { isAwaitingClosure } from './flags';
import { WAITING_DAYS_ALERT_THRESHOLD, type ProjectStatus } from '@/shared/constants/statuses';
import { daysSince } from '@/shared/lib/dates';
import type {
  ProjectTimelineEntry,
  ProjectWithContext,
  TimelineTransitionEntry,
} from '@/shared/types/domain';

/**
 * A esteira desenhada DENTRO do cartão: onde este projeto está no caminho.
 *
 * O crachá de situação diz o NOME do lugar ("Em análise"); esta trilha diz o
 * LUGAR — quanto já andou e quanto falta. É a diferença entre saber que o
 * projeto está em análise e ver que ele é o terceiro de cinco passos, com dois
 * pela frente. Sem isso, acompanhar exige que a pessoa tenha a esteira
 * decorada.
 *
 * ⚠️ **Quatro nós, e não cinco** (ADR-013): "Enviado" saiu. Ele era o mesmo
 * lugar que "Análise" visto de outro ângulo, e um trilho que gasta dois pontos
 * no mesmo lugar faz o projeto parecer mais adiantado do que está.
 *
 * ⚠️ A esteira NÃO é linear (`constants/statuses.ts`), e um trilho reto
 * mentiria. Os três desvios são resolvidos pelo ESTADO do nó, nunca por uma
 * seta para trás — seta para trás num trilho curto vira ruído:
 *
 * - `changes_requested` volta para o ministério: o nó "Análise" é substituído
 *   por "Ajustes solicitados" em atenção, e o trilho para ali. Visualmente é
 *   "empacou neste ponto", que é exatamente o que aconteceu.
 * - `rejected` é saída lateral: o nó "Aprovado" vira "Rejeitado", e o trilho
 *   TERMINA — "Concluído" não é desenhado, porque não vai acontecer. Desenhá-lo
 *   apagado prometeria um futuro que a igreja já recusou.
 * - `cancelled` também termina, e mais cedo: de onde ele saiu (`under_review`,
 *   `changes_requested` ou `approved`) não está em `status`, e inventar o ponto
 *   seria afirmar um percurso que não se sabe.
 */
export type TimelineNodeState =
  /** Já passou. */
  | 'done'
  /** Onde o projeto está agora, no caminho normal. */
  | 'current'
  /** Ainda vai acontecer. */
  | 'upcoming'
  /** Está aqui e a bola é de alguém — `changes_requested`. */
  | 'attention'
  /** Acabou aqui, e não por bem — `rejected` / `cancelled`. */
  | 'stopped';

export interface TimelineNode {
  label: string;
  state: TimelineNodeState;
}

const DRAFT = 'Rascunho';
const REVIEW = 'Análise';
const APPROVED = 'Aprovado';
const COMPLETED = 'Concluído';

/**
 * Os nós da trilha para uma situação. `switch` exaustivo e sem `default`, pelo
 * mesmo motivo de `phases.ts`: situação nova tem de quebrar o build.
 */
export function timelineFor(status: ProjectStatus): readonly TimelineNode[] {
  switch (status) {
    case 'draft':
      return [
        { label: DRAFT, state: 'current' },
        { label: REVIEW, state: 'upcoming' },
        { label: APPROVED, state: 'upcoming' },
        { label: COMPLETED, state: 'upcoming' },
      ];

    case 'under_review':
      return [
        { label: DRAFT, state: 'done' },
        { label: REVIEW, state: 'current' },
        { label: APPROVED, state: 'upcoming' },
        { label: COMPLETED, state: 'upcoming' },
      ];

    case 'changes_requested':
      return [
        { label: DRAFT, state: 'done' },
        { label: 'Ajustes solicitados', state: 'attention' },
        { label: APPROVED, state: 'upcoming' },
        { label: COMPLETED, state: 'upcoming' },
      ];

    case 'approved':
      return [
        { label: DRAFT, state: 'done' },
        { label: REVIEW, state: 'done' },
        { label: APPROVED, state: 'current' },
        { label: COMPLETED, state: 'upcoming' },
      ];

    case 'completed':
      return [
        { label: DRAFT, state: 'done' },
        { label: REVIEW, state: 'done' },
        { label: APPROVED, state: 'done' },
        { label: COMPLETED, state: 'done' },
      ];

    case 'rejected':
      return [
        { label: DRAFT, state: 'done' },
        { label: REVIEW, state: 'done' },
        { label: 'Rejeitado', state: 'stopped' },
      ];

    case 'cancelled':
      return [
        { label: DRAFT, state: 'done' },
        { label: 'Cancelado', state: 'stopped' },
      ];
  }
}

/**
 * Há quanto tempo a bola está parada com alguém — o dado que transforma
 * "está em análise" em "está em análise há 23 dias".
 *
 * É o que se cobra. Sem ele, a trilha diz ONDE o projeto está, mas não que ele
 * está ali tempo demais; e "cobrar os responsáveis" é justamente agir sobre o
 * segundo. `null` quando ninguém está devendo nada: projeto aprovado à espera
 * da própria data não está parado, está agendado.
 */
export interface ProjectWaiting {
  /** Frase pronta; quem escreve português é a interface, nunca o banco. */
  label: string;
  days: number;
  /**
   * Passou do limiar do PRD §6.3 (`WAITING_DAYS_ALERT_THRESHOLD`).
   *
   * ⚠️ **`/projetos` não usa isto, e a recusa é deliberada.** Lá a cor do aviso
   * sai de `needsImmediateAction` (`lib/sections.ts`): contar dias marcaria de
   * âmbar um projeto que só precisa de um empurrão e deixaria passar um
   * encerrado sem relatório ontem, que precisa que alguém sente e escreva.
   *
   * Quem vai consumir este campo é a fila de análise (US-18), que ordena por
   * tempo de espera e exige sinalizar os parados além do limiar — lá o número
   * de dias É o assunto. Enquanto `/analise` não existe, o campo fica calculado
   * e sem consumidor de produção, coberto por teste.
   */
  alert: boolean;
}

/**
 * O que a espera precisa de um projeto: a situação e os três marcos de tempo.
 *
 * Tipo estreito pela mesma razão de `ProjectClockView` em `lib/flags.ts` — e
 * agora com consumidor: a fonte "Projetos" do calendário vem da projeção
 * reduzida de `agenda_projects`, que não carrega `version` nem `created_by`.
 * Nela `submitted_at`/`decided_at` chegam nulos, e a função devolve `null`, que
 * é a verdade: compromisso aprovado não está esperando ninguém.
 */
export type ProjectWaitingView = Pick<
  ProjectWithContext,
  'status' | 'starts_at' | 'ends_at' | 'submitted_at' | 'decided_at'
>;

export function waitingOf(
  project: ProjectWaitingView,
  now: Date = new Date(),
): ProjectWaiting | null {
  // `now` é repassado adiante, e não deixado no padrão: `isAwaitingClosure`
  // também olha o relógio, e um dos dois lendo a hora de verdade tornaria a
  // função dependente de quando ela roda.
  const since = startOfWait(project, now);
  if (!since) return null;

  const days = daysSince(since.at, now);
  // Relógio adiantado ou data futura no banco: contar "há -2 dias" seria pior
  // do que não contar.
  if (days < 0) return null;

  return { label: since.label, days, alert: days >= WAITING_DAYS_ALERT_THRESHOLD };
}

function startOfWait(
  project: ProjectWaitingView,
  now: Date,
): { label: string; at: string } | null {
  switch (project.status) {
    // A espera começa na submissão, e não na criação: é o mesmo marco que a
    // fila de análise usa para ordenar (US-18). O ato ainda se chama enviar,
    // mesmo com o estado se chamando "em análise" (ADR-013, decisão 2).
    case 'under_review':
      return project.submitted_at
        ? { label: 'Aguardando decisão', at: project.submitted_at }
        : null;

    // Aqui o relógio corre contra o ministério, e o marco é a decisão que
    // devolveu o projeto.
    case 'changes_requested':
      return project.decided_at ? { label: 'Aguardando ajuste', at: project.decided_at } : null;

    // Evento aconteceu e ninguém encerrou. É cobrança de relatório, e o marco
    // é o fim do evento.
    case 'approved':
      return isAwaitingClosure(project, now) && project.ends_at
        ? { label: 'Encerrado sem relatório', at: project.ends_at }
        : null;

    // Rascunho é trabalho de uma pessoa só e ninguém cobra; os terminais já
    // acabaram.
    case 'draft':
    case 'rejected':
    case 'cancelled':
    case 'completed':
      return null;
  }
}

/**
 * O ÚLTIMO pedido de ajuste da linha do tempo (decisão 45).
 *
 * **O último, não o histórico** — e a distinção é a decisão. As rodadas
 * anteriores continuam na linha do tempo de `/projetos/:id` (US-24), que é
 * onde se reconstitui como o projeto chegou até aqui. Dentro do formulário, o
 * que a pessoa veio fazer é atender a UM pedido: empilhar os antigos ali
 * transformaria a instrução vigente em arqueologia, e a segunda rodada
 * mostraria dois textos concorrendo pela mesma tarefa.
 *
 * A view `project_timeline` vem ordenada por `created_at` ascendente, então o
 * pedido vigente é a última ocorrência — nunca a primeira.
 */
export function lastChangeRequest(
  entries: readonly ProjectTimelineEntry[],
): TimelineTransitionEntry | null {
  for (let index = entries.length - 1; index >= 0; index -= 1) {
    const entry = entries[index];
    if (entry && entry.kind === 'transition' && entry.to_status === 'changes_requested') {
      return entry;
    }
  }
  return null;
}
